import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useOrderHistory } from 'hooks/context/OrderHistory';
import { InlineCommaList } from 'components/InlineCommaList/InlineCommaList';
import { Banner, BannerVariant } from './Banner';
import { WARNING_REORDER } from 'components/ReorderWarning.const';
import { heapTrack } from 'util/Analytics';

import styles from './ErrorBanner.module.scss';

type ReorderWarningProps = {
  onClick?: () => void;
  className?: string;
};

export const ReorderWarning: FC<ReorderWarningProps> = ({ onClick, className }) => {
  const { t } = useTranslation();
  const { unavailableItems, itemsWithUnavailableModifiers } = useOrderHistory();
  const hasUnavailableItem = unavailableItems?.length > 0;
  const hasItemsWithUnavailableModifiers = itemsWithUnavailableModifiers?.length > 0;

  useEffect(() => {
    if (hasUnavailableItem || hasItemsWithUnavailableModifiers) {
      heapTrack('reorder-item-failure-banner-viewed', {
        type: hasUnavailableItem ? 'unavailable-item' : 'unavailable-modifier',
      });
    }
  }, [hasUnavailableItem, hasItemsWithUnavailableModifiers]);

  if (!hasUnavailableItem && !hasItemsWithUnavailableModifiers) {
    return null;
  }

  return (
    <Banner
      variant={BannerVariant.ALERT}
      rounded={true}
      data-testid={WARNING_REORDER.banner}
      className={clsx(styles.reorderWarning, className)}
    >
      {hasUnavailableItem && (
        <div data-testid={WARNING_REORDER.unavailableItem}>
          <p className={clsx(styles.errorHeader)}>{t('cart.itemUnavailable', { count: unavailableItems.length })}</p>
          <InlineCommaList className={clsx(styles.list, styles.errorContent)}>
            {Object.entries(unavailableItems).map(([key, item]) => (
              <li key={key}>{t('previousOrder.orderItem', { name: item.name, count: 1 })}</li>
            ))}
          </InlineCommaList>
        </div>
      )}
      {hasUnavailableItem && hasItemsWithUnavailableModifiers && <div className={clsx(styles.break)} />}
      {hasItemsWithUnavailableModifiers && (
        <div data-testid={WARNING_REORDER.unavailableModifier}>
          <p className={clsx(styles.errorHeader)}>
            {t('cart.modifierUnavailable', { count: itemsWithUnavailableModifiers.length })}
          </p>
          <InlineCommaList className={clsx(styles.list, styles.errorContent)}>
            {Object.entries(itemsWithUnavailableModifiers).map(([key, item]) => (
              <li key={key}>{t('previousOrder.orderItem', { name: item.name, count: 1 })}</li>
            ))}
          </InlineCommaList>
          <button className={clsx(styles.backToMenu)} onClick={onClick}>
            {t('cart.backToMenu')}
          </button>
        </div>
      )}
    </Banner>
  );
};
